/*****************************************************************
 *  CUSTOMER BUTLER - THE TWO LISTS IN THE HR LOOK                 *
 *  File: apps/web/src/pages/customer/HrListPages.js               *
 *  Addresses: /app/customers and /app/properties (HR look)        *
 *  What it does: the customer list and the property list as HR    *
 *  Butler's roster: a banner card when there is something to act  *
 *  on, search + the primary button, the saved views as chips, the *
 *  count line, then HR's table (cards on a phone).                *
 *  Reads: the layout door, the list doors, the list totals, and   *
 *  (owner / admin only) the Zoho Bridge's waiting count.          *
 *  Writes: a new customer (the same Add sheet the clean look has).*
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE CHIPS A ROW WEARS                              *
 *  SECTION 2 - THE COUNT LINE AND THE BRIDGE BANNER               *
 *  SECTION 3 - THE CUSTOMER LIST                                  *
 *  SECTION 4 - THE PROPERTY LIST                                  *
 *****************************************************************/

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from './customerIcons';
import { HrPageFrame, HrBanner, HrQuietLine } from './HrChrome';
import { HrRecordList } from './HrRecordList';
import { AddCustomerSheet, readOfflineCustomers } from './CustomerListPage';
import { nameForProperty } from './PropertyListPage';
import { useCustomerAccessView } from './customerAccessView';
import {
  fetchLayout, fetchCustomerPage, fetchPropertyPage, fetchListTotals, fetchBridgeWaiting,
} from './customerApi';

/*****************************************************************
 *  SECTION 1 - THE CHIPS A ROW WEARS                              *
 *  One chip per cell, word + icon + meaning colour (colour is     *
 *  never the only signal).                                        *
 *****************************************************************/

// The chip each customer status wears (Customer model: active, prospect,
// inactive, archived).
const CUSTOMER_STATUS_CHIPS = {
  active: { tone: 'ok', icon: 'check', word: 'Active' },
  prospect: { tone: 'brand', icon: 'clock', word: 'Prospect' },
  inactive: { tone: 'neutral', icon: 'close', word: 'Inactive' },
  archived: { tone: 'neutral', icon: 'archive', word: 'Archived' },
};

/* ---- customerStatusChip(customer) -- the one status chip ---- */
export function customerStatusChip(customer) {
  const statusWord = String(customer.status || 'active').toLowerCase();

  return CUSTOMER_STATUS_CHIPS[statusWord] || { tone: 'neutral', icon: 'file', word: customer.status };
}

/* ---- propertyAlertWords(property) -- Zoho's Property Alerts, as a list ---- */
export function propertyAlertWords(property) {
  const customValues = property.customFields || {};
  const storedAlerts = customValues.Property_Alerts;

  if (Array.isArray(storedAlerts)) {
    return storedAlerts.filter(Boolean).map(String);
  }

  return storedAlerts ? [String(storedAlerts)] : [];
}

/* ---- propertyAlertChip(property) -- one chip: how many alerts ---- */
function propertyAlertChip(property) {
  const alertCount = propertyAlertWords(property).length;

  if (alertCount === 0) {
    return { tone: 'neutral', icon: 'check', word: 'No alerts' };
  }

  return { tone: 'warn', icon: 'warn', word: alertCount === 1 ? '1 alert' : `${alertCount} alerts` };
}

/*****************************************************************
 *  SECTION 2 - THE COUNT LINE AND THE BRIDGE BANNER               *
 *****************************************************************/

/* ---- countWords(number) -- 8604 -> "8,604" ---- */
function countWords(number) {
  return Number(number || 0).toLocaleString('en-US');
}

/* ---- CountLine({ totals, nounOne, nounMany }) --------------------
   HR's bold headline: every record that exists, whatever chip is
   on ("312 customers — 40 new this week").                      */
function CountLine({ totals, nounOne, nounMany }) {
  if (!totals) {
    return null;
  }

  const allWords = `${countWords(totals.all)} ${totals.all === 1 ? nounOne : nounMany}`;
  const weekWords = totals.thisWeek === null ? '' : ` — ${countWords(totals.thisWeek)} new this week`;

  return (
    <p className="cb-hr-count cb-hr-countline">
      <b>{allWords}{weekWords}</b>
    </p>
  );
}

/* ---- useListTotals(fetchPageFor, weekViewKey, askedAfterAdd) ---- */
function useListTotals(fetchPageFor, weekViewKey, askedAfterAdd) {
  const [totals, setTotals] = useState(null);

  useEffect(() => {
    let isStillShowing = true; // a late answer must not touch a closed page

    fetchListTotals(fetchPageFor, weekViewKey).then((answer) => {
      if (isStillShowing) {
        setTotals(answer);
      }
    });

    return () => {
      isStillShowing = false;
    };
  }, [fetchPageFor, weekViewKey, askedAfterAdd]);

  return totals;
}

/* ---- useBridgeWaiting() -- the Zoho Bridge's waiting count, or null ---- */
function useBridgeWaiting() {
  const [waiting, setWaiting] = useState(null);

  useEffect(() => {
    let isStillShowing = true;

    fetchBridgeWaiting().then((answer) => {
      if (isStillShowing) {
        setWaiting(answer);
      }
    });

    return () => {
      isStillShowing = false;
    };
  }, []);

  return waiting;
}

/* ---- BridgeBanner({ waitingCount, nounMany }) --------------------
   Only when something really waits, and only to the people the
   Bridge lets in (it answers no to everyone else).              */
function BridgeBanner({ waitingCount, nounMany }) {
  if (!waitingCount) {
    return null;
  }

  const verb = waitingCount === 1 ? 'is' : 'are';
  const title = `${countWords(waitingCount)} ${nounMany} ${verb} waiting at the Zoho Bridge`;

  return (
    <HrBanner
      tone="brand"
      icon="link"
      title={title}
      action={<Link className="bc-btn is-quiet bc-btn-auto" to="/bridge"><Icon name="arrowright" /> Open the Zoho Bridge</Link>}
    >
      They come into Customer Butler when somebody says yes to them on the Zoho Bridge page. Nothing comes in by itself.
    </HrBanner>
  );
}

/*****************************************************************
 *  SECTION 3 - THE CUSTOMER LIST                                  *
 *****************************************************************/

// The Butler columns the list door can filter by (services/listViews.js
// FILTERABLE_COLUMNS); every custom field can be filtered too.
const FILTERABLE_BUILTIN_KEYS = ['displayName', 'firstName', 'lastName', 'primaryEmail', 'primaryPhone', 'companyName', 'source'];

/* ---- filterFieldsFrom(layout) ------------------------------------
   Zoho's "Filter By Fields": every field of the layout the door can
   filter, A to Z by its label, as [{ key, label }].            */
export function filterFieldsFrom(layout) {
  const allFields = layout.sections.flatMap((oneSection) => oneSection.fields);
  const filterable = allFields.filter((oneField) => oneField.kind === 'custom' || FILTERABLE_BUILTIN_KEYS.includes(oneField.key));
  const byLabel = [...filterable].sort((first, second) => String(first.label).localeCompare(String(second.label)));

  return byLabel.map((oneField) => ({ key: oneField.key, label: oneField.label }));
}

// Zoho's card row lines (frame 080): Phone, Email, Account/Company Name.
const CUSTOMER_CARD_LINES = [
  { label: 'Phone', column: { key: 'primaryPhone', from: 'builtin' } },
  { label: 'Email', column: { key: 'primaryEmail', from: 'builtin' } },
  { label: 'Account/Company Name', column: { key: 'Account_Name', from: 'custom' } },
];

// A property's card lines (no Zoho Property list was on screen; these
// are the property list's own columns).
const PROPERTY_CARD_LINES = [
  { label: 'County', column: { key: 'County', from: 'custom' } },
  { label: 'Zip code', column: { key: 'zip', from: 'builtin' } },
  { label: 'Owner', column: { key: 'ownerName', from: 'builtin' } },
];

/* ---- HrCustomerListPage({ onChooseSkin }) ------------------------ */
export function HrCustomerListPage({ onChooseSkin }) {
  const [layout, setLayout] = useState(null);
  const [layoutProblem, setLayoutProblem] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [listVersion, setListVersion] = useState(0);    // bumps to reload the list after an add
  const [savedWords, setSavedWords] = useState('');
  const accessView = useCustomerAccessView();          // Look up = no Add (Honest UI, 2026-10-14)
  const totals = useListTotals(fetchCustomerPage, 'new-this-week', listVersion);
  const bridgeWaiting = useBridgeWaiting();

  useEffect(() => {
    fetchLayout('Customer')
      .then(setLayout)
      .catch(() => setLayoutProblem('The customer list could not be loaded. Check the connection and try again.'));
  }, []);

  // A fresh function each time the list must reload, so the list asks again.
  const fetchPage = useCallback((listChoice) => {
    const askedAfterAdd = listVersion;

    return fetchCustomerPage({ ...listChoice, askedAfterAdd, withNextTask: true });
  }, [listVersion]);

  function finishAdding() {
    setIsAdding(false);
    setSavedWords('Customer added. Open it to fill in the rest of the fields.');
    setListVersion((versionNow) => versionNow + 1);
  }

  const addButton = accessView.mayWrite ? (
    <button type="button" className="bc-btn is-primary bc-btn-auto" onClick={() => setIsAdding(true)}>
      <Icon name="plus" size="button" /> Add customer
    </button>
  ) : null;

  const bridgeBanner = bridgeWaiting ? <BridgeBanner waitingCount={bridgeWaiting.contacts} nounMany="contacts" /> : null;

  return (
    <HrPageFrame onChooseSkin={onChooseSkin}>
      {savedWords ? <HrBanner tone="ok" icon="check">{savedWords}</HrBanner> : null}
      {layoutProblem ? <HrBanner tone="bad" icon="warn">{layoutProblem}</HrBanner> : null}
      {!layout && !layoutProblem ? <HrQuietLine icon="clock">Loading</HrQuietLine> : null}

      {layout ? (
        <HrRecordList
          layout={layout}
          fetchPage={fetchPage}
          fetchOffline={readOfflineCustomers}
          linkFor={(row) => `/customers/${row.id}`}
          nameFor={(row) => row.displayName || 'Unnamed customer'}
          underFor={(row) => row.primaryEmail || row.primaryPhone || ''}
          shownElsewhereKeys={['displayName', 'primaryEmail']}
          chipHeading="Status"
          chipsFor={(row) => [customerStatusChip(row)]}
          searchHint="Search by name, email, phone or company"
          nounOne="customer"
          nounMany="customers"
          addButton={addButton}
          railTitle="Filter Customers by"
          filterFields={filterFieldsFrom(layout)}
          cardLines={CUSTOMER_CARD_LINES}
          countLine={<CountLine totals={totals} nounOne="customer" nounMany="customers" />}
          banner={bridgeBanner}
        />
      ) : null}

      {isAdding && accessView.mayWrite ? <AddCustomerSheet onClose={() => setIsAdding(false)} onSaved={finishAdding} /> : null}
    </HrPageFrame>
  );
}

/*****************************************************************
 *  SECTION 4 - THE PROPERTY LIST                                  *
 *  A property is added from its customer's record, so it always   *
 *  has an owner; the list's primary place says so instead of      *
 *  offering a button that would make an owner-less property.      *
 *****************************************************************/

/* ---- propertyUnderLine(row) -- the street under the name (when it is not the name) ---- */
function propertyUnderLine(row) {
  const streetLine = [row.addressLine1, row.city, row.state].filter(Boolean).join(', ');
  const nameWords = nameForProperty(row);

  return streetLine && streetLine !== nameWords ? streetLine : '';
}

/* ---- HrPropertyListPage({ onChooseSkin }) ------------------------ */
export function HrPropertyListPage({ onChooseSkin }) {
  const [layout, setLayout] = useState(null);
  const [layoutProblem, setLayoutProblem] = useState('');
  const totals = useListTotals(fetchPropertyPage, null, 0);
  const bridgeWaiting = useBridgeWaiting();

  useEffect(() => {
    fetchLayout('Property')
      .then(setLayout)
      .catch(() => setLayoutProblem('The property list could not be loaded. Check the connection and try again.'));
  }, []);

  const addHint = (
    <span className="cb-hr-hunt-hint">
      <Icon name="home" /> Add a property from its customer's record
    </span>
  );

  const bridgeBanner = bridgeWaiting ? <BridgeBanner waitingCount={bridgeWaiting.properties} nounMany="properties" /> : null;

  return (
    <HrPageFrame onChooseSkin={onChooseSkin}>
      {layoutProblem ? <HrBanner tone="bad" icon="warn">{layoutProblem}</HrBanner> : null}
      {!layout && !layoutProblem ? <HrQuietLine icon="clock">Loading</HrQuietLine> : null}

      {layout ? (
        <HrRecordList
          layout={layout}
          fetchPage={fetchPropertyPage}
          linkFor={(row) => `/properties/${row.id}`}
          nameFor={nameForProperty}
          underFor={propertyUnderLine}
          shownElsewhereKeys={['label', 'Property_Alerts']}
          chipHeading="Alerts"
          chipsFor={(row) => [propertyAlertChip(row)]}
          searchHint="Search by property name, street, city or zip"
          nounOne="property"
          nounMany="properties"
          addButton={addHint}
          railTitle="Filter Properties by"
          filterFields={null}
          cardLines={PROPERTY_CARD_LINES}
          countLine={<CountLine totals={totals} nounOne="property" nounMany="properties" />}
          banner={bridgeBanner}
        />
      ) : null}
    </HrPageFrame>
  );
}

const HrListPages = { HrCustomerListPage, HrPropertyListPage };

export default HrListPages;
